import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ListChecks, Lock } from "@phosphor-icons/react";
import { personName, usePortalIdentity } from "../auth/usePortalIdentity";
import { useCurrentManagement, type CurrentManagement } from "../data/managementQueries";
import { loginEndsOn } from "../lib/managementDates";
import { STATUS_LABEL, useRequests, useServices, type ClientRequest } from "../data/requestQueries";
import { useIncidents, useMonitors } from "../data/statusQueries";
import {
  useAnalyticsConnections,
  useAnalyticsMonthlyVisits,
  useAnalyticsPageMetrics,
  useAnalyticsSnapshots,
  type AnalyticsMonthlyVisits
} from "../data/analyticsQueries";
import {
  changeSentence,
  checkedAt,
  causeLine,
  lastDisruption,
  monthBars,
  otherOpenRequests,
  siteRows,
  sourceShares,
  visitChange,
  visitsViews,
  waitingOnClient,
  type Disruption,
  type SiteRow,
  type VisitsView
} from "../data/dashboard";
import { Badge, Quiet } from "../ui/Badge";
import { BoxState } from "../ui/BoxState";
import { Hint } from "../ui/Hint";
import { RowLink, RowList } from "../ui/RowLinks";
import { StatusWord } from "./clientRequests/requestWords";
import { formatLongDay, formatShortDay, formatWhen, inSentence } from "./clientOps/format";
import { monthWords, useClientReports, type ClientReport } from "../data/reportQueries";
import "./overview/overview.css";
import { MonthBars } from "../ui/MonthBars";
import { useClientPath } from "../data/readSource";

/**
 * Översikt, the client's dashboard, since build round 4 (2026-09-27).
 *
 * Built to the sketch Mattej approved on 2026-09-26 (`prototype/kund/pages.js` `overviewPage`) and to
 * plan.md section 5.1 as rewritten the same day. The boxes show their content instead of a row per
 * area that links on, and Analys och drift is gone as a destination because everything it held is
 * here. In phone order: Förvaltning, Behöver er uppmärksamhet, Besök, Webbplatsen, Ärenden, Varifrån
 * besökarna kommer, Mest besökta sidor. On a computer the same order fills a grid whose rows are as
 * tall as their tallest box (`overview.css`).
 *
 * The sketch's rules, and where each one is kept:
 *
 * - **A number is shown in one place.** The 30-day total is in Besök and nowhere else, and unread chat
 *   messages are a count on Chatt in the navigation (`AppShell`), no longer a row here.
 * - **Normal is a grey word or nothing.** Förvaltning is a box only when paused or cancelled; Uppe is a
 *   grey word; a working analytics connection has no sentence, only its figures.
 * - **Empty is quiet.** A box with nothing to show is not drawn: no attention box when nothing waits,
 *   no sources without a site tag, no top list without pages.
 * - **No explanatory paragraph.** A rule that matters is one line behind a question mark: the period
 *   and the capture time of the figures, and that monitoring is not an SLA.
 *
 * The logic is in `app/data/dashboard.ts`; this file draws it. Every box has its own loading and its
 * own failure, so a read that failed is never shown as a calm fact and never takes another box with it.
 */

const counted = new Intl.NumberFormat("sv-SE");
const timeOnly = new Intl.DateTimeFormat("sv-SE", { hour: "2-digit", minute: "2-digit" });
const longDate = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "long", year: "numeric" });

/** A `date` column as "31 oktober 2026", built from its parts so no timezone moves it a day. */
function dayWithYear(value: string | null): string | null {
  const parts = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  return parts ? longDate.format(new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))) : null;
}

/** "02:53" today, "i går 23:10" or "21 sep 02:53" otherwise, for "Nere sedan". */
function sinceWords(value: string, now: Date = new Date()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  if (date.toDateString() === now.toDateString()) {
    return timeOnly.format(date);
  }
  return inSentence(formatWhen(value) ?? "");
}

/** docs/polish.md P-15, word for word. */
const PART_FAILED = "Den här delen gick inte att hämta just nu. Resten av sidan stämmer. Försök igen.";

/** A box that could not be read: its title stays, and one compact retry stands where its rows would. */
function FailedBox({ title, onRetry, className }: { title: string; onRetry: () => void; className?: string }) {
  return (
    <section className={`panel${className ? ` ${className}` : ""}`}>
      <header className="panel-head">
        <h2>{title}</h2>
      </header>
      <BoxState kind="error" compact title={PART_FAILED} onRetry={onRetry} />
    </section>
  );
}

function LoadingBox({ title, className }: { title: string; className?: string }) {
  return (
    <section className={`panel${className ? ` ${className}` : ""}`} aria-busy="true">
      <header className="panel-head">
        <h2>{title}</h2>
      </header>
      <BoxState kind="loading" compact title="Hämtar…" />
    </section>
  );
}

/* --- Förvaltning --------------------------------------------------------------------------------- */

function ManagementBox({ status, management }: { status: string | null; management: CurrentManagement | null }) {
  const clientPath = useClientPath();
  if (status === "paused") {
    return (
      <section className="panel k-callout k-span" aria-labelledby="management-title" data-testid="dash-management">
        <header className="panel-head">
          <h2 id="management-title">
            <Lock size={18} aria-hidden="true" /> Förvaltningen är pausad
          </h2>
          <Link className="button is-quiet" to={clientPath("/settings/management")}>
            Varför
          </Link>
        </header>
        <div className="panel-body">
          <p>Ni kan läsa allt men inte skicka något.</p>
        </div>
      </section>
    );
  }

  if (status !== "ending" && status !== "read_only") {
    return null;
  }

  const ends = management ? loginEndsOn(management) : null;
  const endsWords = ends ? longDate.format(ends) : null;
  const runsTo = dayWithYear(management?.scheduled_end_on ?? null);

  return (
    <section className="panel k-callout k-span" aria-labelledby="management-title" data-testid="dash-management">
      <header className="panel-head">
        <h2 id="management-title">
          {status === "ending" ? "Förvaltningen är uppsagd" : "Förvaltningen är avslutad"}
          <Hint>Efter slutdatumet kan ni läsa i portalen i 30 dagar. Sedan går det inte att logga in längre.</Hint>
        </h2>
        <Link className="button is-quiet" to={clientPath("/settings/management")}>
          Förvaltning
        </Link>
      </header>
      <div className="panel-body">
        <p data-testid="dash-management-line">
          {status === "ending" && runsTo ? `Löper till och med ${runsTo}. ` : ""}
          {endsWords
            ? `Inloggningen slutar ${endsWords}.`
            : "Vi kan inte visa när inloggningen slutar. Fråga i chatten."}
        </p>
      </div>
    </section>
  );
}

/* --- Behöver er uppmärksamhet -------------------------------------------------------------------- */

function AttentionBox({ requests }: { requests: ClientRequest[] }) {
  const clientPath = useClientPath();
  if (requests.length === 0) {
    return null;
  }

  return (
    <section className="panel k-att k-span" aria-labelledby="attention-title" data-testid="attention">
      <header className="panel-head">
        <h2 id="attention-title">Behöver er uppmärksamhet</h2>
      </header>
      <RowList label="Behöver er uppmärksamhet">
        {requests.map((request) => (
          <RowLink
            key={request.id}
            to={clientPath(`/requests/${request.request_number}`)}
            icon={ListChecks}
            tone="warn"
            trail={<Badge tone="warn">Ert svar</Badge>}
            testId="attention-link"
          >
            <span className="primary-cell">
              <strong>{request.title ?? `Ärende REQ-${request.request_number}`}</strong>
              <span>
                <span className="mono" data-testid="attention-request-number">
                  REQ-{request.request_number}
                </span>{" "}
                · Petrovic Systems väntar på ert besked
              </span>
            </span>
          </RowLink>
        ))}
      </RowList>
    </section>
  );
}

/* --- Besök ----------------------------------------------------------------------------------------- */

function VisitsBox({ view, months }: { view: VisitsView; months: AnalyticsMonthlyVisits[] }) {
  const clientPath = useClientPath();
  const { snapshot, serviceName, connection } = view;
  const titleId = `visits-${connection.id}`;

  const from = formatLongDay(snapshot?.period_start ?? null);
  const to = formatLongDay(snapshot?.period_end ?? null);
  const captured = formatWhen(snapshot?.captured_at ?? null);
  /*
    The period and the capture time behind the question mark, not as visible text (plan.md section
    5.1, 2026-09-26). The cache lives at most a day, so the time is still one press away.
  */
  const hint = [
    "Räknat utan kakor. Vi ser inga enskilda besökare och inga IP-adresser.",
    "Staplarna börjar den månad analysen kopplades in.",
    from && to ? `Perioden är ${from} till och med ${to}.` : "",
    captured ? `Hämtat ${inSentence(captured)}.` : ""
  ]
    .filter(Boolean)
    .join(" ");

  const head = (
    <header className="panel-head">
      <h2 id={titleId}>
        Besök
        <Hint>{hint}</Hint>
      </h2>
      {serviceName ? <span className="quiet k-small">{serviceName}</span> : null}
    </header>
  );

  if (!snapshot) {
    // plan.md section 5.5's neutral unavailable state, and emphatically not a zero.
    return (
      <section className="panel k-visits" aria-labelledby={titleId} data-testid="dash-visits" data-state="unavailable">
        {head}
        <p className="k-line" data-testid="dash-visits-unavailable">
          Inga aktuella siffror just nu.
        </p>
      </section>
    );
  }

  const change = visitChange(snapshot.visits, snapshot.visits_previous);
  const bars = monthBars(months, connection.id).map((bar) => ({
    month: bar.month,
    short: bar.short,
    spoken: bar.visits === null ? `${bar.long}: inte mätt` : `${bar.long}: ${counted.format(bar.visits)} besök`,
    value: bar.visits,
    current: bar.current
  }));

  return (
    <section className="panel k-visits" aria-labelledby={titleId} data-testid="dash-visits" data-state="figures">
      {head}
      <div className="panel-body">
        <p className="k-figure-line">
          <span className="k-figure" data-testid="dash-visits-figure">
            {counted.format(snapshot.visits)}
          </span>
          <span>besök de senaste 30 dagarna</span>
        </p>
        {change ? (
          <p className="quiet" data-testid="dash-visits-change">
            {changeSentence(change)}
          </p>
        ) : null}
        {snapshot.mobile_share !== null ? (
          <p className="quiet" data-testid="dash-visits-mobile">
            {snapshot.mobile_share} % i mobilen
          </p>
        ) : null}
        <MonthBars bars={bars} label="Besök per månad, de senaste 12 månaderna" testId="dash-bars" barTestId="dash-bar" />
        {/* Build round 37, 2026-10-11: every figure, compared month by month, on a page of its own. */}
        <p className="k-line">
          <Link to={clientPath("/statistics")} data-testid="dash-statistics-link">
            Se all statistik
          </Link>
        </p>
      </div>
    </section>
  );
}

/* --- Webbplatsen ----------------------------------------------------------------------------------- */

function SiteBox({ rows, disruption, checked }: { rows: SiteRow[]; disruption: Disruption | null; checked: string | null }) {
  const anyDown = rows.some((row) => row.state === "down");
  const checkedWords = checked ? formatWhen(checked) : null;

  return (
    <section className="panel" aria-labelledby="site-title" data-testid="dash-site">
      <header className="panel-head">
        <h2 id="site-title">
          Webbplatsen
          <Hint>Vi kontrollerar var femte minut. Det är inte ett servicenivåavtal och inget löfte om en åtgärdstid. Hastigheten mäts hos era riktiga besökare.</Hint>
        </h2>
        {checked && checkedWords ? (
          <span className="quiet k-small" data-testid="dash-site-checked">
            Kontrollerat <time dateTime={checked}>{inSentence(checkedWords)}</time>
          </span>
        ) : null}
      </header>
      <ul className="k-rows">
        {rows.map((row) => (
          <li className="k-site" key={row.serviceId} data-state={row.state ?? "unwatched"} data-testid="dash-site-row">
            <span className="primary-cell">
              <strong>{row.name ?? "Er webbplats"}</strong>
              {row.speed ? <span data-testid="dash-site-speed">Hastighet: {row.speed}</span> : null}
              {row.state === "down" && row.cause ? <span data-testid="dash-site-cause">Orsak: {row.cause}</span> : null}
            </span>
            {row.state === "down" ? (
              <Badge tone="danger" testId="dash-site-state">
                {row.downSince ? `Nere sedan ${sinceWords(row.downSince)}` : "Nere"}
              </Badge>
            ) : row.state === "up" ? (
              <Quiet testId="dash-site-state">Uppe</Quiet>
            ) : row.endedAt ? (
              <Quiet testId="dash-site-state">Avslutad {formatLongDay(row.endedAt)}</Quiet>
            ) : null}
          </li>
        ))}
      </ul>
      {disruption && !anyDown ? (
        <footer className="panel-foot k-last-incident">
          <span className="quiet k-small" data-testid="dash-last-disruption">
            Senaste störning: {disruption.serviceName ?? "er webbplats"},{" "}
            {formatShortDay(disruption.incident.detected_at)}
            {disruption.duration ? `, ${disruption.duration}` : ""}. {causeLine(disruption.incident)}
          </span>
        </footer>
      ) : null}
    </section>
  );
}

/* --- Ärenden ------------------------------------------------------------------------------------- */

function RequestsBox({ requests }: { requests: ClientRequest[] }) {
  const clientPath = useClientPath();
  return (
    <section className="panel" aria-labelledby="requests-title" data-testid="dash-requests">
      <header className="panel-head">
        <h2 id="requests-title">Ärenden</h2>
        <Link className="button is-quiet" to={clientPath("/requests")}>
          Alla ärenden
        </Link>
      </header>
      {requests.length === 0 ? (
        <p className="k-line" data-testid="dash-requests-none">
          Inget annat öppet ärende.
        </p>
      ) : (
        <RowList label="Öppna ärenden">
          {requests.map((request) => (
            <RowLink
              key={request.id}
              to={clientPath(`/requests/${request.request_number}`)}
              trail={<StatusWord status={request.status} />}
              testId="dash-request"
            >
              <span className="k-req">
                <span className="mono quiet">REQ-{request.request_number}</span>
                <strong>{request.title ?? STATUS_LABEL[request.status]}</strong>
              </span>
            </RowLink>
          ))}
        </RowList>
      )}
    </section>
  );
}

/* --- Månadsrapport -------------------------------------------------------------------------------- */

/**
 * The newest monthly report, build round B2 (2026-10-09). Drawn only when there is one: a client on
 * Förvaltning has none, and an empty box would be the dead click the sketch's rules remove. The
 * proposals not yet answered are counted, because they are the part that waits on the client.
 */
function ReportBox({ report }: { report: ClientReport }) {
  const clientPath = useClientPath();
  const open = report.proposals.filter((proposal) => proposal.request_number === null).length;

  return (
    <section className="panel" aria-labelledby="report-title" data-testid="dash-report">
      <header className="panel-head">
        <h2 id="report-title">Månadsrapport</h2>
        <Link className="button is-quiet" to={clientPath("/reports")} data-testid="dash-report-open">
          Läs rapporten
        </Link>
      </header>
      <p className="k-line">
        Rapporten för {monthWords(report.month)}.
        {open > 0 ? ` ${open === 1 ? "Ett förslag väntar" : `${open} förslag väntar`} på ert svar.` : ""}
      </p>
    </section>
  );
}

/* --- Varifrån besökarna kommer, Mest besökta sidor ---------------------------------------------- */

function SourcesBox({ view }: { view: VisitsView }) {
  const shares = sourceShares(view.snapshot?.sources ?? []);
  if (shares.length === 0) {
    return null;
  }
  const titleId = `sources-${view.connection.id}`;

  return (
    <section className="panel" aria-labelledby={titleId} data-testid="dash-sources">
      <header className="panel-head">
        <h2 id={titleId}>
          Varifrån besökarna kommer
          <Hint>Direkt betyder att adressen skrevs in, eller att sidan som länkade inte sa vem den var.</Hint>
        </h2>
      </header>
      <ul className="k-shares">
        {shares.map((share) => (
          <li key={share.label} data-testid="dash-source">
            <span>{share.label}</span>
            <span className="k-share" aria-hidden="true">
              <i style={{ width: `${share.percent}%` }} />
            </span>
            <span className="num">{share.percent} %</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PagesBox({ view }: { view: VisitsView }) {
  if (view.pages.length === 0) {
    return null;
  }
  const titleId = `pages-${view.connection.id}`;

  return (
    <section className="panel" aria-labelledby={titleId} data-testid="dash-pages">
      <header className="panel-head">
        <h2 id={titleId}>Mest besökta sidor</h2>
        <span className="quiet k-small" aria-hidden="true">
          visningar
        </span>
      </header>
      <ol className="k-toplist">
        {view.pages.map((page) => (
          <li key={page.id} data-testid="dash-page">
            <span className="k-mono">{page.page_path}</span>
            <span className="num">
              {counted.format(page.page_views)}
              <span className="visually-hidden"> visningar</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* --- the page -------------------------------------------------------------------------------------- */

function Page({ children }: { children: ReactNode }) {
  return <div className="k-page is-wide is-dash">{children}</div>;
}

export function OverviewRoute() {
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;

  const management = useCurrentManagement(tenantId);
  const requests = useRequests(tenantId);
  const services = useServices(tenantId);
  const monitors = useMonitors(tenantId);
  const incidents = useIncidents(tenantId);
  const connections = useAnalyticsConnections(tenantId);
  const snapshots = useAnalyticsSnapshots(tenantId);
  const pages = useAnalyticsPageMetrics(tenantId);
  const months = useAnalyticsMonthlyVisits(tenantId);
  const reports = useClientReports(tenantId);

  if (identity.isLoading) {
    return (
      <Page>
        <section className="panel">
          <BoxState kind="loading" title="Hämtar er översikt" />
        </section>
      </Page>
    );
  }

  if (identity.isError) {
    return (
      <Page>
        <section className="panel">
          <BoxState
            kind="error"
            title="Översikten kunde inte hämtas"
            body="Ladda om sidan. Står felet kvar, hör av er till Petrovic Systems."
            onRetry={() => void identity.refetch()}
          />
        </section>
      </Page>
    );
  }

  const data = identity.data;

  if (!data?.tenantId) {
    return (
      <Page>
        <section className="panel">
          <BoxState
            kind="denied"
            title="Ingen aktiv åtkomst"
            body="Ditt konto är inte kopplat till något kundkonto just nu. Kontakta Petrovic Systems om det ser fel ut."
          />
        </section>
      </Page>
    );
  }

  const greeting = personName(identity);

  /* The requests feed two boxes, so one read decides both. */
  const requestBoxes = requests.isLoading ? (
    <LoadingBox title="Ärenden" />
  ) : requests.isError ? (
    <FailedBox title="Ärenden" onRetry={() => void requests.refetch()} />
  ) : null;
  const requestRows = requests.data ?? [];

  /* Analytics: connections, snapshots, pages and months are one set of figures. */
  const analyticsLoading =
    connections.isLoading || snapshots.isLoading || pages.isLoading || months.isLoading || services.isLoading;
  const analyticsFailed = connections.isError || snapshots.isError || pages.isError || months.isError || services.isError;
  const retryAnalytics = () => {
    void connections.refetch();
    void snapshots.refetch();
    void pages.refetch();
    void months.refetch();
    void services.refetch();
  };
  const views = analyticsLoading || analyticsFailed
    ? []
    : visitsViews(connections.data ?? [], snapshots.data ?? [], pages.data ?? [], services.data ?? []);

  /* Webbplatsen reads the monitors, the incidents, the services and the speed from the figures. */
  const siteLoading = monitors.isLoading || incidents.isLoading || services.isLoading;
  const siteFailed = monitors.isError || incidents.isError || services.isError;
  const sites = siteLoading || siteFailed ? [] : siteRows(services.data ?? [], monitors.data ?? [], incidents.data ?? [], views);
  const disruption = siteLoading || siteFailed ? null : lastDisruption(incidents.data ?? [], services.data ?? []);

  const visitsBoxes: ReactNode = analyticsLoading ? (
    <LoadingBox title="Besök" className="k-visits" />
  ) : analyticsFailed ? (
    <FailedBox title="Besök" className="k-visits" onRetry={retryAnalytics} />
  ) : (
    views.map((view) => <VisitsBox key={view.connection.id} view={view} months={months.data ?? []} />)
  );

  let siteBox: ReactNode = null;
  if (siteLoading) {
    siteBox = <LoadingBox title="Webbplatsen" />;
  } else if (siteFailed) {
    siteBox = (
      <FailedBox
        title="Webbplatsen"
        onRetry={() => {
          void monitors.refetch();
          void incidents.refetch();
          void services.refetch();
        }}
      />
    );
  } else if (sites.length > 0 || disruption) {
    siteBox = <SiteBox rows={sites} disruption={disruption} checked={checkedAt(monitors.data ?? [])} />;
  }

  return (
    <Page>
      <header className="k-greeting">
        {/*
          No name means no comma. The greeting used to end `?? "där"` and read "Välkommen, där" to a
          real client whose profile row had never been created.
        */}
        <h1 id="overview-title">{greeting.kind === "named" ? `Välkommen, ${greeting.first}` : "Välkommen"}</h1>
        <p className="quiet">Här är läget för {data.tenantDisplayName}.</p>
      </header>

      <div className="k-dash" data-testid="dashboard">
        {/*
          The status comes from the identity, the same value the write controls follow, so this box and
          the composer can never disagree about whether the client can write. The dates come from the
          current period, which Inställningar › Förvaltning shares.
        */}
        <ManagementBox status={data.managementStatus} management={management.data ?? null} />
        {requestBoxes ? null : <AttentionBox requests={waitingOnClient(requestRows)} />}
        {reports.isError ? (
          <FailedBox title="Månadsrapport" onRetry={() => void reports.refetch()} />
        ) : reports.data && reports.data.length > 0 ? (
          <ReportBox report={reports.data[0]} />
        ) : null}
        {visitsBoxes}
        {siteBox}
        {requestBoxes ?? <RequestsBox requests={otherOpenRequests(requestRows)} />}
        {views.map((view) => (
          <SourcesBox key={`sources-${view.connection.id}`} view={view} />
        ))}
        {views.map((view) => (
          <PagesBox key={`pages-${view.connection.id}`} view={view} />
        ))}
      </div>
    </Page>
  );
}
